import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import Mark from '../components/Mark';
import { useT, translate } from '../i18n/i18n';

type State = 'working' | 'done' | 'failed';

/**
 * WHERE THE LINK IN THE EMAIL NOW LANDS.
 *
 * It used to go to Keycloak, which confirmed the address on a dark panel
 * headed "Email address validation" and offered a bare "Click here to
 * proceed" — a product the reader has never heard of, appearing in the middle
 * of signing up to one they have.
 */
export default function VerifyPage() {
  const t = useT();
  const [state, setState] = useState<State>('working');
  const [message, setMessage] = useState<string>('');
  // React runs effects twice in development; without this the key is spent
  // once and the second attempt reports an expired link.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const key = new URLSearchParams(window.location.search).get('token');
    if (!key) {
      setState('failed');
      setMessage(translate('This link is incomplete. Try opening it again from your email.'));
      return;
    }

    (async () => {
      try {
        await api<void>('/auth/verify', { method: 'POST', body: { key } });
        setState('done');
      } catch (err) {
        setState('failed');
        setMessage(err instanceof Error ? err.message : translate('We could not confirm your address.'));
      } finally {
        /* THE KEY DOES NOT BELONG IN THE ADDRESS BAR afterwards: it is the one
           secret in this page, and it would otherwise sit in history and in
           anything the browser syncs. */
        window.history.replaceState(null, '', '/verify');
      }
    })();
  }, []);

  return (
    <section aria-labelledby="verify-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="card">
        <h2 id="verify-heading">
          <Mark className="brand-mark" />
          {state === 'working' && t('Confirming your email')}
          {state === 'done' && t('Email confirmed')}
          {state === 'failed' && t('We could not confirm this')}
        </h2>

        {state === 'working' && (
          <p aria-live="polite">{t('One moment — we are confirming your address.')}</p>
        )}

        {state === 'done' && (
          <>
            <p aria-live="polite">
              {t('Thank you. Your email address is confirmed and your account is ready.')}
            </p>
            <div className="toolbar">
              <Link className="btn" to="/login">{t('Sign in')}</Link>
            </div>
          </>
        )}

        {state === 'failed' && (
          <>
            <div className="error" role="alert">{message}</div>
            <p className="field-hint">
              {t('Verification links do not last forever. Signing up again with the same email address sends a fresh one — it will not create a second account.')}
            </p>
            <div className="toolbar">
              <Link className="btn" to="/login">{t('Back to sign in')}</Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
